import ApplicationController from './application_controller';

// Async chat UI for /admin/ai-usage/discussions/:id.
//
// Behavior:
//   - Enter (without Shift) submits the form. Shift+Enter inserts a newline.
//   - Submitting appends the user's message bubble immediately, clears the
//     textarea, and appends a placeholder assistant bubble with a spinner
//     ("Thinking…").
//   - The send POSTs to the send-message endpoint with Accept: application/json.
//     The server runs the model call synchronously (10–30s) and returns the
//     assistant text inline.
//   - When the response arrives, the placeholder gets replaced with the real
//     assistant bubble. On failure, the placeholder becomes a red error bubble.
//   - Scrolls the message body to the bottom as bubbles are appended.
export default class extends ApplicationController {
  static targets = ['messages', 'textarea', 'emptyState', 'form'];
  static values = {
    url: String,
    userName: String,
    csrf: String,
    // Optional JSON blob merged into every send as `page`. KIKI Helps uses it to tell the model
    // which screen the user is looking at; the admin discussions view omits it entirely.
    context: Object,
  };

  connect() {
    // Server-rendered history: assistant bubbles arrive as escaped raw markdown tagged with
    // data-gai-md; render them to HTML here so history and live bubbles look identical.
    this.element.querySelectorAll('[data-gai-md]').forEach((el) => {
      el.innerHTML = this.renderMarkdown(el.textContent);
      el.removeAttribute('data-gai-md');
    });
    // Defer to the next frame so the layout has settled (icon font, etc.)
    // before measuring scrollHeight.
    requestAnimationFrame(() => this.scrollToBottom());
  }

  handleKeydown(event) {
    if (event.key !== 'Enter') return;
    if (event.shiftKey || event.ctrlKey || event.metaKey) return;
    event.preventDefault();
    this.submit(event);
  }

  async submit(event) {
    event.preventDefault();
    const text = (this.textareaTarget.value || '').trim();
    if (!text) return;
    if (this.busy) return;
    this.busy = true;
    this.textareaTarget.disabled = true;

    if (this.hasEmptyStateTarget) {
      this.emptyStateTarget.remove();
    }

    this.appendBubble({ role: 'user', content: text });
    this.textareaTarget.value = '';
    const placeholder = this.appendBubble({ role: 'assistant', pending: true });
    this.scrollToBottom();

    try {
      const response = await fetch(this.urlValue, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': this.csrfValue || this.csrfFromMeta(),
        },
        body: JSON.stringify({ message: text, page_context: this.contextValue }),
      });

      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        const errText = payload.error || `HTTP ${response.status}`;
        placeholder.replaceWith(this.buildBubble({ role: 'assistant', failed: true, error: errText }));
      } else {
        placeholder.replaceWith(this.buildBubble({
          role: 'assistant',
          content: payload.content || '',
          durationMs: payload.duration_ms,
        }));
      }
    } catch (err) {
      placeholder.replaceWith(this.buildBubble({ role: 'assistant', failed: true, error: err.message }));
    } finally {
      this.busy = false;
      this.textareaTarget.disabled = false;
      this.textareaTarget.focus();
      this.scrollToBottom();
    }
  }

  appendBubble(msg) {
    const bubble = this.buildBubble(msg);
    this.messagesTarget.appendChild(bubble);
    return bubble;
  }

  buildBubble(msg) {
    const isUser = msg.role === 'user';
    const align = isUser ? 'right' : 'left';
    const bubbleBg = isUser ? '#2c4ad7' : '#ffffff';
    const bubbleColor = isUser ? '#ffffff' : 'inherit';
    const bubbleBorder = isUser ? 'none' : '1px solid #dee2e6';

    // Pure inline styles to match the server-rendered ERB in show.html.erb
    // and sidestep any Ambiki SCSS rule that might override display or width
    // on a class-based bubble.
    const row = document.createElement('div');
    row.style.cssText = 'margin-bottom: 0.5rem; text-align: ' + align + ';';

    // Copy-to-clipboard on real messages (mirrors the server-rendered history in
    // show.html.erb): the row hosts the clipboard controller, the bubble is the source, and
    // the button sits left of the user's name / right of "Ambiki AI". Stimulus connects the
    // controller automatically when the row is inserted.
    const copyable = !msg.pending && !msg.failed && (msg.content || '').trim() !== '';
    const copyButton =
      '<button type="button" class="btn btn-link btn-sm p-0 text-muted" title="Copy message" data-action="clipboard#copy">' +
      '<i class="bi bi-clipboard" data-clipboard-target="icon"></i></button>';
    if (copyable) row.setAttribute('data-controller', 'clipboard');

    const label = document.createElement('div');
    label.style.cssText = 'font-size: 0.85em; color: #6c757d; margin-bottom: 0.25rem; padding: 0 0.25rem; text-align: ' + align + ';';
    if (isUser) {
      const name = this.escapeHtml(this.userNameValue || 'You');
      label.innerHTML = copyable ? '<span class="mr-1">' + copyButton + '</span>' + name : name;
    } else {
      let html = '<i class="bi bi-stars mr-1"></i> Ambiki AI';
      const durationLabel = this.formatDuration(msg.durationMs);
      if (durationLabel) {
        html += ' <small class="text-muted">(' + durationLabel + ')</small>';
      }
      if (copyable) html += ' <span class="ml-1">' + copyButton + '</span>';
      label.innerHTML = html;
    }
    row.appendChild(label);

    // Assistant replies render as markdown (block layout, white-space normal); user messages
    // stay literal text with pre-wrap.
    const isMd = !isUser && copyable;
    const bubble = document.createElement('span');
    bubble.style.cssText =
      'display: inline-block;' +
      'max-width: 70%;' +
      'padding: 0.5rem 0.75rem;' +
      'border-radius: 0.5rem;' +
      'white-space: ' + (isMd ? 'normal' : 'pre-wrap') + ';' +
      'word-break: break-word;' +
      'text-align: left;' +
      'background-color: ' + bubbleBg + ';' +
      'color: ' + bubbleColor + ';' +
      'border: ' + bubbleBorder + ';';

    if (copyable) bubble.setAttribute('data-clipboard-target', 'source');
    if (msg.pending) {
      bubble.innerHTML =
        '<span class="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>' +
        '<em class="text-muted">Thinking…</em>';
    } else if (msg.failed) {
      bubble.innerHTML =
        '<i class="bi bi-exclamation-triangle text-danger mr-1"></i>' +
        '<em class="text-danger">Reply failed: ' + this.escapeHtml(msg.error || 'unknown error') + '</em>';
    } else if (isMd) {
      bubble.classList.add('gai-md');
      bubble.innerHTML = this.renderMarkdown(msg.content || '');
    } else {
      bubble.textContent = msg.content || '';
    }
    row.appendChild(bubble);
    return row;
  }

  // Minimal, safe markdown for chat replies. The input is HTML-ESCAPED FIRST, so every tag in
  // the output is one we emit ourselves — no dependency, no injection surface. Supports what
  // the model actually produces: #-headings, **bold**, *italic*, `code`, fenced code blocks,
  // -/1. lists, [text](url) links (see safeHref for which destinations are allowed),
  // blank-line paragraphs.
  renderMarkdown(raw) {
    const esc = this.escapeHtml(String(raw || '').trim());
    let html = '';
    let list = null; // 'ul' | 'ol'
    let para = [];
    let code = null; // collecting lines inside a ``` fence
    const closeList = () => {
      if (list) { html += '</' + list + '>'; list = null; }
    };
    const flushPara = () => {
      if (para.length) { html += '<p>' + para.join('<br>') + '</p>'; para = []; }
    };
    esc.split(/\r?\n/).forEach((line) => {
      if (code !== null) {
        if (/^```/.test(line.trim())) { html += '<pre><code>' + code.join('\n') + '</code></pre>'; code = null; }
        else code.push(line);
        return;
      }
      const t = line.trim();
      if (/^```/.test(t)) { flushPara(); closeList(); code = []; return; }
      if (t === '') { flushPara(); closeList(); return; }
      const heading = t.match(/^(#{1,6})\s+(.*)$/);
      if (heading) {
        flushPara(); closeList();
        const lvl = Math.min(heading[1].length + 2, 6);
        html += '<h' + lvl + '>' + this.renderInline(heading[2]) + '</h' + lvl + '>';
        return;
      }
      const bullet = t.match(/^[-*]\s+(.*)$/);
      if (bullet) {
        flushPara();
        if (list !== 'ul') { closeList(); html += '<ul>'; list = 'ul'; }
        html += '<li>' + this.renderInline(bullet[1]) + '</li>';
        return;
      }
      const num = t.match(/^\d+[.)]\s+(.*)$/);
      if (num) {
        flushPara();
        if (list !== 'ol') { closeList(); html += '<ol>'; list = 'ol'; }
        html += '<li>' + this.renderInline(num[1]) + '</li>';
        return;
      }
      closeList();
      para.push(this.renderInline(t));
    });
    flushPara();
    closeList();
    if (code !== null) html += '<pre><code>' + code.join('\n') + '</code></pre>';
    return html;
  }

  // Inline transforms over already-escaped text.
  //
  // Links open in a new tab, including same-site ones. The panel's whole premise is that you
  // read help while the page behind it stays live, so navigating the window out from under a
  // half-filled visit note would defeat the feature.
  renderInline(escaped) {
    return escaped
      .replace(/`([^`]+)`/g, '<code>$1</code>')
      .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
      .replace(/(^|\s)\*([^*\s][^*]*)\*(?=\s|[.,;:!?)]|$)/g, '$1<em>$2</em>')
      .replace(/\[([^\]]+)\]\(([^)\s]*)\)/g, (_whole, text, url) => {
        const href = this.safeHref(url);
        if (!href) return text;
        // A mail link has nothing to show in a tab; asking for one leaves a blank one behind.
        const target = /^mailto:/i.test(href) ? '' : ' target="_blank" rel="noopener"';
        return `<a href="${href}"${target}>${text}</a>`;
      });
  }

  // The href for a markdown link, or null when we cannot vouch for where it goes.
  //
  // Three shapes are allowed, which is everything a help answer legitimately cites: an
  // absolute http(s) URL, a same-site path, and a mailto address. The path case is the one
  // that matters day to day — the assistant cites FAQ articles as "/help/faqs/…", and those
  // are most of the links it produces. A leading "//" (or "/\", which browsers normalise to
  // the same thing) is rejected, because that reads as protocol-relative and leaves the site.
  //
  // Anything else — javascript:, data:, a bare word — returns null, and renderInline keeps
  // the link text as plain text rather than rendering a destination we do not trust.
  safeHref(url) {
    if (!/^(https?:\/\/\S|mailto:\S|\/(?![/\\]))/i.test(url)) return null;
    // escapeHtml covers & < >, but leaves quotes alone: they are only dangerous inside an
    // attribute value, which is exactly where this is headed.
    return url.replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  scrollToBottom() {
    const container = this.messagesTarget?.parentElement;
    if (container) container.scrollTop = container.scrollHeight;
  }

  csrfFromMeta() {
    const meta = document.querySelector('meta[name="csrf-token"]');
    return meta?.content || '';
  }

  escapeHtml(s) {
    const div = document.createElement('div');
    div.textContent = String(s);
    return div.innerHTML;
  }

  formatDuration(ms) {
    if (!ms || Number.isNaN(Number(ms))) return null;
    let sec = Math.round(Number(ms) / 1000);
    if (sec < 1) sec = 1;
    return sec + ' ' + (sec === 1 ? 'second' : 'seconds');
  }
}
